// @flow
import React from 'react';
import katex from 'katex';
import 'katex/dist/katex.css';
import './katex-mathml-fix.css';
import { usePropsValidation } from './validation/runtime-checks/index.js';
import { types as t } from './validation/runtime-checks/index.js';
import { ContentContext } from './JsxContent.js';

import fontMetricsData from 'katex/src/fontMetricsData.js';

// ⬚ doesn't come with pre-computed font metrics, so Katex doesn't know how
// to do layout correctly. This causes katex expressions on subsequent line
// boxes to overlap each other. Here we add some directionally correct metrics
// for ⬚ (We've used the width of the W char in Main-Regular as the approximate
// size for both width and height). To do a proper job we'd need to be more
// disciplined about which font will actually render the char (most fonts
// don't support it, so the OS is using fallback logic to find a font that does).
// On MacOS Monterey, it falls back to HiraginoSans-W3, for example. Windows
// is almost certainly going to have a much shitter/different font it falls back
// to. Different font selected means different font metrics. Further if we
// wanted precise metrics we'd use a script with similar logic to this:
// https://github.com/KaTeX/KaTeX/blob/main/src/metrics/extract_ttfs.py
// All that is a bit too onerous for the moment. This approximate solution works
// well enough (at least on MacOS) for now.
const newFontMetrics = {
  ...fontMetricsData['Main-Regular'],
  ['⬚'.charCodeAt(0)]: [0, 1, 0, 0, 1],
};
katex.__setFontMetrics('Main-Regular', newFontMetrics);
// We also need to register the symbol with KaTeX
// See: https://github.com/KaTeX/KaTeX/blob/main/src/symbols.js#L55
katex.__defineSymbol('math', 'main', 'textord', '⬚', '⬚');

type Props = {|
  expression: string,
  // LaTeX has a notion of inline vs display mode. Inline mode tries to prevent
  // vertical growth as much as possible to prevent awful looking line heights,
  // since it is designed for being interleaved between paragraph text. You'll
  // notis in fractions, sub/superscripts of \Sigma, etc.
  // Display mode renders better when your math expressions are not interleaved in paragraphs.
  displayMode?: boolean,
|};

// We intentionally take the latex expression as a prop, since latex
// uses curly braces {} which is used in JSX for interpolation. Disambiguating
// this for the parser is not something we want to inflict on content authors.
const Latex = (props: Props) => {
  const { baseFontSize } = React.useContext(ContentContext);

  const { failedValidation } = usePropsValidation(props, Latex);
  const { expression, displayMode = false } = props;
  const ref = React.useRef(null);
  React.useLayoutEffect(() => {
    if (ref.current === null) return;
    katex.render(expression, ref.current, {
      throwOnError: false,
      displayMode,
    });
  }, [expression, displayMode]);
  if (failedValidation) return null;
  // Katex renders at 1.21 em, so this makes it render at baseFontSize px
  return (
    <span style={{ fontSize: baseFontSize / 1.21 }} ref={ref}>
      {expression}
    </span>
  );
};
Latex.spec = {
  expression: t.latex,
  displayMode: t.optional(t.boolean),
};

export default Latex;
